import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "~/atoms/button";
import { updateCompanyGroup } from "~/api/v2/requests";
import { type CompanyGroup } from "~/api/types";
import { Input } from "~/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import i18n from "~/i18n";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";

interface Props {
  isOpen: boolean;
  group: CompanyGroup;
  onSubmit: () => void;
  onClose: () => void;
}

const RenameCompanyGroupSchema = z.object({
  name: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(($) => $.validations.required),
    )
    .max(255),
});

type RenameCompanyGroupValues = z.infer<typeof RenameCompanyGroupSchema>;

export const RenameCompanyGroupModal = ({
  isOpen,
  group,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(RenameCompanyGroupSchema),
    values: { name: group.name },
  });

  const handleSubmit = async (values: RenameCompanyGroupValues) => {
    try {
      setIsLoading(true);
      await updateCompanyGroup(group.id, { name: values.name });
      toast.success(t(($) => $.toasts.success.companyGroupSaved));
      onSubmit();
      onClose();
    } catch {
      toast.error(t(($) => $.toasts.error.companyGroupNotSaved));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(($) => $.companyGroups.editName)}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(($) => $.companyGroups.create.nameLabel)}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isLoading}>
                {t(($) => $.commons.buttons.confirm)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
